import React, { useEffect, useState, useCallback } from "react";
import ReusableMuiTable from "../../components/common/Table/Table";
import Box from "@mui/material/Box";
import { IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Button from "@mui/material/Button";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { getAttribute, deleteAttribute } from "../../api/service/attribute.service";
import { showToast } from "../../components/common/ToastNotification/ToastNotification";
import Loader from "../../components/Loader/Loader";

const MIN_LOADER_TIME = 500;

const Attribute = () => {
  const navigate = useNavigate();

  const [list, setList] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);


  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 500);

    return () => clearTimeout(t);
  }, [search]);

  const fetchAttributes = useCallback(async () => {
    setLoading(true);
    const start = Date.now();

    try {
      const res = await getAttribute(debouncedSearch, page, 10);
      setList(res?.data?.data || []);
      setTotalPages(res?.data?.last_page || 1);
    } catch (e) {
      console.error(e);
    } finally {
      const elapsed = Date.now() - start;
      const delay = Math.max(MIN_LOADER_TIME - elapsed, 0);
      setTimeout(() => setLoading(false), delay);
    }
  }, [debouncedSearch, page]);

  useEffect(() => {
    fetchAttributes();
  }, [fetchAttributes]);

  const handleDelete = (row) => {
    Swal.fire({
      title: "Are you sure to delete this attribute?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#C39B22",
      cancelButtonColor: "#d32f2f",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (!result.isConfirmed) return;

      try {
        setLoading(true);

        let res = await deleteAttribute(row.id);

        showToast.success(res?.message || "Attribute deleted");
        fetchAttributes();
      } catch (error) {
        console.error(error);
        showToast.error(error?.message || "Failed to delete attribute");
      }
    });
  };


  const columns = [
    { name: "page_name", label: "Page Name" },
    { name: "attribute", label: "Attribute" },
    { name: "value", label: "Value" },
    {
      name: "action",
      label: "Action",
      options: {
        search: false,
        sort: false,
        customBodyRender: (_, meta) => {
          const row = list[meta.rowIndex];
          return (
            <Box sx={{ display: "flex", gap: 1 }}>
              <IconButton
                size="small"
                onClick={() => navigate(`/update-attribute/${row.id}`)}
              >
                <EditIcon sx={{ color: "#d4af37" }} fontSize="small" />
              </IconButton>

              <IconButton
                size="small"
                onClick={() => handleDelete(row)}
              >
                <DeleteIcon sx={{ color: "#d32f2f" }} fontSize="small" />
              </IconButton>
            </Box>

          );
        },
      },
    },
  ];

  return (
    <>
      {loading && <Loader />}

      <ReusableMuiTable
        title="Attribute"
        data={list}
        columns={columns}
        options={{
          serverSide: true,
          search: true,
          page: page - 1,
          rowsPerPage: 10,
          count: totalPages * 10,

          onSearchChange: (text) => setSearch(text || ""),
          onChangePage: (p) => setPage(p + 1),
          onTableChange: (action) => action === "searchClose" && setSearch(""),
        }}
        customToolbarRight={
          <Button
            variant="contained"
            onClick={() => navigate("/add-attribute")}
            sx={{ backgroundColor: "#C39B22", textTransform: "none", fontWeight: 600 }}
          >
            Add Attribute
          </Button>
        }
      />
    </>
  );
};

export default Attribute;
